import type { RequestFn } from '../client';

/** Index discriminator returned for each search result group. */
export type SearchIndex =
  | 'payment_attempts'
  | 'payment_intents'
  | 'refunds'
  | 'disputes'
  | 'payouts'
  | 'sessionizer_payment_attempts'
  | 'sessionizer_payment_intents'
  | 'sessionizer_refunds'
  | 'sessionizer_disputes'
  | 'routing_rules'
  | 'webhook_events'
  | 'audit_logs'
  | 'subscriptions';

export type SearchStatus = 'Success' | 'Failure';

/** One result group (per index) in the response array. */
export interface SearchGroupResponse {
  count: number;
  index: SearchIndex;
  hits: Record<string, unknown>[];
  status: SearchStatus;
}

/**
 * The window a global search covers. The documented wire fields are
 * `start_time` (required) and `end_time` (optional — omit it for "up to
 * now"); the server also accepts the camelCase spellings as aliases, which
 * earlier SDK versions sent, so both are declared and either compiles.
 * Prefer the snake_case pair: it is what the operation documents.
 */
export type SearchTimeRange =
  | { start_time: string; end_time?: string | null }
  /** @deprecated Use `start_time` / `end_time` — the documented wire names. */
  | { startTime: string; endTime?: string | null };

export interface GlobalSearchRequest {
  query: string;
  filters?: Record<string, unknown>;
  /** Naive ISO 8601 bounds; `end_time` may be omitted. */
  timeRange?: SearchTimeRange;
  /** Wire alias for `timeRange` — the server reads either. */
  time_range?: SearchTimeRange;
}

/** Global cross-index search. */
export class Search {
  constructor(private readonly request: RequestFn) {}

  /**
   * Search every supported index for `query`.
   * `POST /analytics/search`
   *
   * @example
   * ```typescript
   * const groups = await delopay.search.global({ query: 'pay_abc' });
   * for (const g of groups) console.log(g.index, g.count);
   * ```
   */
  async global(
    params: GlobalSearchRequest,
    options?: { signal?: AbortSignal },
  ): Promise<SearchGroupResponse[]> {
    return this.request('POST', '/analytics/search', {
      body: params,
      ...(options?.signal ? { signal: options.signal } : {}),
    });
  }
}
